import "@/styles/escaperoom/interface.scss";

import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useKeyboardControls } from "@react-three/drei";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { BiCross, BiSearch } from "react-icons/bi";
import { FaRegHandRock } from "react-icons/fa";

import { useStateContext } from "@/context/context-provider";

import Timer from "./timer";

const SortableItem = ({ slot, isFocused, isDragging, onClick }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: slot.name });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        backgroundImage: slot.image ? `url(${slot.image})` : null,
        cursor: isDragging ? "grabbing" : "pointer",
    };

    return (
        <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            className={`item ${isFocused ? "active" : ""}`}
            style={style}
            onClick={onClick}>
            {slot.count > 1 && <span className="item-count">{slot.count}</span>}
        </div>
    );
};

const ItemInputDisplay = () => {
    const { itemInputs } = useStateContext();
    // useEffect(() => {
    //     console.log("itemInputs", itemInputs);
    // }, [itemInputs]);
    if (itemInputs.length === 0) {
        return null;
    }
    const grouped = useMemo(() => {
        const map = {};
        for (const item of itemInputs) {
            const name = item?.name ?? "unknown";
            if (map[name]) {
                map[name].count += 1;
            } else {
                map[name] = { name, image: item.image, count: 1, representative: item };
            }
        }
        return map;
    }, [itemInputs]);
    return (
        <div className="item-input-display">
            {Object.values(grouped).map((input) => (
                <div key={input.name} className="item item--input" style={{ backgroundImage: input.image ? `url(${input.image})` : null }}>
                    {input.count > 1 && <span className="item-count">{input.count}</span>}
                </div>
            ))}
        </div>
    );
};
export const Interface = () => {
    const { focusedItem, cursor, setFocusedItem, menu, solved, pickedup } = useStateContext();

    const grouped = useMemo(() => {
        const map = {};
        for (const item of pickedup) {
            const name = item?.name ?? "unknown";
            if (map[name]) {
                map[name].count += 1;
            } else {
                map[name] = { name, image: item.image, count: 1, representative: item };
            }
        }
        return map;
    }, [pickedup]);

    const [slots, setSlots] = useState(() => Object.keys(grouped));
    const prevSlotsRef = useRef(slots);

    useEffect(() => {
        const newNames = Object.keys(grouped);
        setSlots((prev) => {
            const kept = prev.filter((n) => newNames.includes(n));
            const added = newNames.filter((n) => !kept.includes(n));
            return [...kept, ...added];
        });
    }, [grouped]);

    useEffect(() => {
        if (slots.length === 0) {
            if (focusedItem) {
                setFocusedItem(null);
            }
            prevSlotsRef.current = [];
            return;
        }

        const currentName = focusedItem?.name;
        const currentSlotExists = currentName && slots.includes(currentName);

        if (currentSlotExists) {
            const slot = grouped[currentName];
            if (slot?.representative) {
                const isStillPickedUp = pickedup.some((item) => item.uuid === focusedItem?.uuid);
                if (!isStillPickedUp || focusedItem?.uuid !== slot.representative.uuid) {
                    setFocusedItem(slot.representative);
                }
            }
        } else {
            const oldSlots = prevSlotsRef.current;
            const oldIndex = currentName ? oldSlots.indexOf(currentName) : -1;
            const safeOldIndex = Math.max(oldIndex, 0);
            const targetIndex = Math.min(safeOldIndex, slots.length - 1);

            const targetSlotName = slots[targetIndex];
            const targetSlot = grouped[targetSlotName];

            if (targetSlot?.representative) {
                setFocusedItem(targetSlot.representative);
            }
        }

        prevSlotsRef.current = slots;
    }, [slots, focusedItem, grouped, pickedup, setFocusedItem]);

    // Drag and Drop Settings
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 10 },
        })
    );

    const [isDragging, setIsDragging] = useState(false);

    const handleDragStart = () => setIsDragging(true);

    const handleDragEnd = (event) => {
        setIsDragging(false);
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = slots.indexOf(active.id);
        const newIndex = slots.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const newSlots = arrayMove(slots, oldIndex, newIndex);
        setSlots(newSlots);
    };

    /**************************************** Inventory Controls **************************************/
    const item1 = useKeyboardControls((state) => state.item1);
    const item2 = useKeyboardControls((state) => state.item2);
    const item3 = useKeyboardControls((state) => state.item3);
    const item4 = useKeyboardControls((state) => state.item4);
    const item5 = useKeyboardControls((state) => state.item5);
    const item6 = useKeyboardControls((state) => state.item6);
    const item7 = useKeyboardControls((state) => state.item7);
    const item8 = useKeyboardControls((state) => state.item8);
    const item9 = useKeyboardControls((state) => state.item9);

    useEffect(() => {
        const handler = (e) => {
            if (slots.length === 0) {
                return;
            }

            let index = slots.indexOf(focusedItem?.name);
            if (index === -1) {
                index = 0;
            }

            index = e.deltaY > 0 ? (index + 1) % slots.length : (index - 1 + slots.length) % slots.length;

            const slotName = slots[index];
            const slot = grouped[slotName];
            if (slot?.representative) {
                setFocusedItem(slot.representative);
            }
        };

        document.addEventListener("wheel", handler);
        return () => document.removeEventListener("wheel", handler);
    }, [focusedItem, slots, grouped, setFocusedItem]);

    const hotkeys = useMemo(
        () => [item1, item2, item3, item4, item5, item6, item7, item8, item9],
        [item1, item2, item3, item4, item5, item6, item7, item8, item9]
    );

    useEffect(() => {
        const pressedIndex = hotkeys.findIndex(Boolean);
        if (pressedIndex !== -1 && slots[pressedIndex]) {
            const slotName = slots[pressedIndex];
            const slot = grouped[slotName];
            if (slot?.representative && slot.representative.name !== focusedItem?.name) {
                setFocusedItem(slot.representative);
            }
        }
    }, [hotkeys, slots, grouped, focusedItem, setFocusedItem]);

    const isAllSolved = useMemo(() => {
        if (!solved || Object.keys(solved).length === 0) {
            return false;
        }
        return Object.values(solved).every((val) => (typeof val === "object" && val !== null ? Boolean(val.isSolved) : Boolean(val)));
    }, [solved]);

    return (
        <>
            {/* Cursor */}
            {cursor === "grab" ? (
                <FaRegHandRock className="cursor" />
            ) : cursor === "inspect" ? (
                <BiSearch className="cursor" />
            ) : (
                <BiCross className="cursor" />
            )}
            {/* ItemInputDisplay */}
            <ItemInputDisplay />

            {/* Inventory */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <SortableContext items={slots} strategy={horizontalListSortingStrategy}>
                    <div className="inventory">
                        {slots.map((name) => {
                            const slot = grouped[name] || { name, count: 0, image: null };
                            return (
                                <SortableItem
                                    key={name}
                                    slot={slot}
                                    isFocused={name === focusedItem?.name}
                                    onClick={() => {
                                        if (slot.representative && slot.representative.name !== focusedItem?.name) {
                                            setFocusedItem(slot.representative);
                                        }
                                    }}
                                    isDragging={isDragging}
                                />
                            );
                        })}
                    </div>
                </SortableContext>
            </DndContext>

            {/* Timer */}
            <Timer paused={menu || isAllSolved} />
        </>
    );
};

export default Interface;
